import React, { useState, useEffect, useCallback } from 'react';
import './StudentAchievements.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaTrophy,
  FaNewspaper,
  FaArrowLeft,
  FaPlus,
  FaTrash,
  FaEdit,
  FaSpinner,
  FaFilePdf,
  FaImage,
  FaExternalLinkAlt,
  FaTimes
} from 'react-icons/fa';

const MAX_FILE_SIZE = 6 * 1024 * 1024; // 6 MB

const SUBTYPE_OPTIONS = [
  { value: 'paper', label: 'Paper' },
  { value: 'poster', label: 'Poster' },
  { value: 'publication', label: 'Publication' }
];
// Records saved before the subType field existed have subType = null — treat as 'publication'.
const SUBTYPE_LABELS = { paper: 'Paper', poster: 'Poster', publication: 'Publication' };
const subTypeLabel = (subType) => SUBTYPE_LABELS[subType] || 'Publication';

const StudentAchievements = ({ onNavigate, type }) => {
  const isAward = type === 'award';
  const label = isAward ? 'Award' : 'Paper / Poster / Publication';
  const labelPlural = isAward ? 'Awards' : 'Papers / Posters / Publications';
  const Icon = isAward ? FaTrophy : FaNewspaper;
  const accentColor = isAward ? '#F59E0B' : '#1565c0';

  const token = localStorage.getItem('token');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');
  const [signedUrls, setSignedUrls] = useState({});

  // Form state
  const [editingItem, setEditingItem] = useState(null); // null = adding, otherwise the item being edited
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [subType, setSubType] = useState('publication');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/achievements?type=${type}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setItems(data.achievements || []);
      }
    } catch (err) {
      console.error('Error fetching achievements:', err);
    } finally {
      setLoading(false);
    }
  }, [token, type]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const getSignedUrl = async (key, id) => {
    if (signedUrls[id]) return signedUrls[id];
    try {
      const res = await fetch(
        `${collegeConfig.api.baseURL}/upload/signed-url?key=${encodeURIComponent(key)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setSignedUrls(prev => ({ ...prev, [id]: data.url }));
        return data.url;
      }
    } catch (err) {
      console.error('Error getting signed URL:', err);
    }
    return null;
  };

  const handleViewFile = async (item) => {
    const url = await getSignedUrl(item.fileKey, item._id);
    if (url) window.open(url, '_blank');
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    setFileError('');
    if (!selected) { setFile(null); return; }

    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowed.includes(selected.type)) {
      setFileError('Only PDF and image files (JPG, PNG, GIF, WEBP) are allowed.');
      setFile(null);
      e.target.value = '';
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      setFileError('File must be smaller than 6 MB.');
      setFile(null);
      e.target.value = '';
      return;
    }
    setFile(selected);
  };

  const resetForm = () => {
    setEditingItem(null);
    setTitle('');
    setDescription('');
    setSubType('publication');
    setFile(null);
    setFileError('');
    setError('');
    setShowForm(false);
  };

  const openAddForm = () => {
    resetForm();
    setShowForm(true);
  };

  const openEditForm = (item) => {
    setEditingItem(item);
    setTitle(item.title || '');
    setDescription(item.description || '');
    setSubType(item.subType || 'publication');
    setFile(null);
    setFileError('');
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required.'); return; }
    setError('');
    setSaving(true);

    let fileKey;
    let fileName;
    let fileType;

    if (file) {
      setUploading(true);
      try {
        let uploadFile = file;
        if (file.type.startsWith('image/')) {
          try {
            const imageCompression = (await import('browser-image-compression')).default;
            uploadFile = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true });
          } catch { /* fallback to original */ }
        }
        const formData = new FormData();
        formData.append('file', uploadFile, file.name);
        const uploadRes = await fetch(`${collegeConfig.api.baseURL}/upload/attachment`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        if (!uploadRes.ok) throw new Error('Upload failed');
        const uploadData = await uploadRes.json();
        fileKey = uploadData.attachment.key;
        fileName = uploadData.attachment.name;
        fileType = uploadData.attachment.type;
      } catch (err) {
        setError('File upload failed. Please try again.');
        setSaving(false);
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    const isPublication = type === 'publication';
    const payload = {
      title: title.trim(),
      description: description.trim(),
      ...(isPublication ? { subType } : {}),
      ...(file ? { fileKey, fileName, fileType } : {})
    };

    try {
      let res, data;
      if (editingItem) {
        res = await fetch(`${collegeConfig.api.baseURL}/achievements/${editingItem._id}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Failed to save');
        // Old attachment was replaced — remove it from storage
        if (file && editingItem.fileKey && editingItem.fileKey !== data.achievement.fileKey) {
          fetch(`${collegeConfig.api.baseURL}/upload/attachment`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ key: editingItem.fileKey })
          }).catch(() => {});
        }
        setItems(prev => prev.map(i => i._id === data.achievement._id ? data.achievement : i));
      } else {
        res = await fetch(`${collegeConfig.api.baseURL}/achievements`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ type, ...payload })
        });
        data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Failed to save');
        setItems(prev => [data.achievement, ...prev]);
      }
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    setDeleting(item._id);
    try {
      // Delete file from storage if present
      if (item.fileKey) {
        await fetch(`${collegeConfig.api.baseURL}/upload/attachment`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ key: item.fileKey })
        });
      }
      await fetch(`${collegeConfig.api.baseURL}/achievements/${item._id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(prev => prev.filter(i => i._id !== item._id));
    } catch (err) {
      alert('Delete failed. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="stuach-loading">
        <FaSpinner className="stuach-spinner" />
        <p>Loading {labelPlural.toLowerCase()}...</p>
      </div>
    );
  }

  return (
    <div className="stuach-container">
      {/* Header */}
      <div className="stuach-header">
        <button className="stuach-back-btn" onClick={() => onNavigate('dashboard')}>
          <FaArrowLeft /> Back
        </button>
        <div className="stuach-header-title">
          <Icon className="stuach-header-icon" style={{ color: accentColor }} />
          <h2>My {labelPlural}</h2>
        </div>
        <button
          className="stuach-add-btn"
          style={{ background: accentColor }}
          onClick={openAddForm}
        >
          <FaPlus /> Add {label}
        </button>
      </div>

      {/* Add / Edit Form Modal */}
      {showForm && (
        <div className="stuach-modal-overlay" onClick={resetForm}>
          <div className="stuach-modal" onClick={e => e.stopPropagation()}>
            <div className="stuach-modal-header">
              <h3>{editingItem ? 'Edit' : 'Add'} {label}</h3>
              <button className="stuach-modal-close" onClick={resetForm}><FaTimes /></button>
            </div>
            <form onSubmit={handleSubmit} className="stuach-form">
              <div className="stuach-field">
                <label>Name / Title <span className="stuach-required">*</span></label>
                <input
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder={`e.g. ${isAward ? 'Best Research Award 2024' : 'Journal of Medicine, Vol. 12'}`}
                  maxLength={200}
                />
              </div>
              {!isAward && (
                <div className="stuach-field">
                  <label>Category <span className="stuach-required">*</span></label>
                  <select
                    className="stuach-select"
                    value={subType}
                    onChange={e => setSubType(e.target.value)}
                  >
                    {SUBTYPE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="stuach-field">
                <label>Description</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder={`Brief details about this ${label.toLowerCase()}...`}
                  rows={3}
                  maxLength={1000}
                />
              </div>
              <div className="stuach-field">
                <label>Attachment <span className="stuach-hint">(PDF or Image, max 6 MB)</span></label>
                {editingItem?.fileKey && !file && (
                  <p className="stuach-current-file">
                    Current file: <strong>{editingItem.fileName || 'Attachment'}</strong> — choose a new file below to replace it.
                  </p>
                )}
                <div className="stuach-file-area">
                  <input
                    type="file"
                    id="stuach-file-input"
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                    onChange={handleFileChange}
                    className="stuach-file-input"
                  />
                  <label htmlFor="stuach-file-input" className="stuach-file-label">
                    {file ? (
                      <span className="stuach-file-selected">
                        {file.type === 'application/pdf' ? <FaFilePdf /> : <FaImage />}
                        {' '}{file.name}
                      </span>
                    ) : (
                      <span>Choose file (optional)</span>
                    )}
                  </label>
                </div>
                {fileError && <p className="stuach-file-error">{fileError}</p>}
              </div>
              {error && <p className="stuach-form-error">{error}</p>}
              <div className="stuach-form-actions">
                <button type="button" className="stuach-cancel-btn" onClick={resetForm}>Cancel</button>
                <button
                  type="submit"
                  className="stuach-submit-btn"
                  style={{ background: accentColor }}
                  disabled={saving}
                >
                  {saving ? (
                    <><FaSpinner className="stuach-spin" /> {uploading ? 'Uploading...' : 'Saving...'}</>
                  ) : `Save ${label}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* List */}
      {items.length === 0 ? (
        <div className="stuach-empty">
          <Icon style={{ fontSize: 48, color: '#ccc', marginBottom: 12 }} />
          <p>No {labelPlural.toLowerCase()} added yet.</p>
          <button
            className="stuach-add-btn"
            style={{ background: accentColor }}
            onClick={openAddForm}
          >
            <FaPlus /> Add your first {label}
          </button>
        </div>
      ) : (
        <div className="stuach-list">
          {items.map(item => (
            <div key={item._id} className="stuach-card" style={{ borderLeftColor: accentColor }}>
              <div className="stuach-card-top">
                <div className="stuach-card-icon-wrap" style={{ background: accentColor + '18' }}>
                  <Icon style={{ color: accentColor, fontSize: 18 }} />
                </div>
                <div className="stuach-card-body">
                  <h4 className="stuach-card-title">
                    {item.title}
                    {!isAward && (
                      <span className="stuach-subtype-badge" style={{ color: accentColor, borderColor: accentColor }}>
                        {subTypeLabel(item.subType)}
                      </span>
                    )}
                  </h4>
                  {item.description && (
                    <p className="stuach-card-desc">{item.description}</p>
                  )}
                  <span className="stuach-card-date">Added {formatDate(item.createdAt)}</span>
                </div>
                <div className="stuach-card-actions">
                  <button
                    className="stuach-edit-btn"
                    onClick={() => openEditForm(item)}
                    title="Edit"
                  >
                    <FaEdit />
                  </button>
                  <button
                    className="stuach-delete-btn"
                    onClick={() => handleDelete(item)}
                    disabled={deleting === item._id}
                    title="Delete"
                  >
                    {deleting === item._id
                      ? <FaSpinner className="stuach-spin" />
                      : <FaTrash />}
                  </button>
                </div>
              </div>
              {item.fileKey && (
                <div className="stuach-card-file">
                  <span className="stuach-file-badge">
                    {item.fileType === 'pdf' ? <FaFilePdf /> : <FaImage />}
                    {' '}{item.fileName || 'Attachment'}
                  </span>
                  <button
                    className="stuach-view-btn"
                    onClick={() => handleViewFile(item)}
                  >
                    <FaExternalLinkAlt /> View
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentAchievements;
